<template>
  <div>
    <div class="row mb-3" :class="showCompanyFields ? 'pb-3 border-bottom' : ''">
      <div :class="showCompanyFields ? 'col-12 col-lg-8' : 'col-12'">
        <div class="row">
          <BushelTextInput v-model="formData.name" label-active="Location Name" :error="submitted && !formData.name" class="col-12 col-lg-6" />
          <BushelSelect
            :value="formData.operation_type"
            no-deselect
            label="name"
            class="col-12 col-lg-6"
            label-active="Location Type"
            :options="operationTypes"
            :error="submitted && !formData.operation_type"
            :loading="!operationTypes.length"
            @select="(selected) => (formData.operation_type = selected)"
          />
          <BushelTextArea v-if="showCompanyFields" v-model="formData.summary" :rows="3" thin class="col-12" label-active="About Us" />
        </div>
      </div>
      <div v-if="showCompanyFields" class="col-12 col-lg-4">
        <slot name="logo" />
      </div>
    </div>
    <div class="row">
      <BushelTextInput v-model="formData.line1" :error="submitted && !formData.line1" label-active="Address Line 1" class="col-12 col-lg-6" />
      <BushelTextInput v-model="formData.line2" label-active="Address Line 2" class="col-12 col-lg-6" />
    </div>
    <div class="row pb-3 mb-3 border-bottom">
      <BushelTextInput v-model="formData.city" :error="submitted && !formData.city" label-active="City" class="col-12 col-lg-3" />
      <BushelSelect
        :value="formData.country"
        class="col-12 col-lg-3"
        label="text"
        label-active="Country"
        :error="submitted && !formData.country"
        no-deselect
        :disabled="usCannabisUser"
        :loading="countriesLoading"
        :options="countries"
        @select="
          (selected) => {
            formData.country = selected;
            formData.state = null;
            fetchStates(selected.id);
          }
        "
      />
      <BushelSelect
        :value="formData.state"
        class="col-12 col-lg-3"
        label="text"
        label-active="State"
        :error="submitted && !formData.state"
        no-deselect
        :disabled="usCannabisUser"
        :loading="statesLoading"
        :options="states"
        @select="(selected) => (formData.state = selected)"
      />
      <BushelTextInput v-model="formData.zip" :error="submitted && !formData.zip" label-active="Zip" class="col-12 col-lg-3" />
    </div>
    <div class="row">
      <div class="col-12 col-lg-6">
        <BushelTextInput
          v-model="formData.state_license"
          class="mb-3"
          :error="submitted && stateLicenseRequired && !formData.state_license"
          :label-active="stateLicenseRequired ? 'State License Number' : 'State License Number (optional)'"
        />
        <SingleFileUploader
          id="state-license-uploader-mobile"
          input-id="stateLicenseDocMobile"
          class="d-block d-lg-none"
          :file-url="formData.state_license_doc"
          no-overlay
          :allow-delete="true"
          download-path="buyers/state-license-doc/download"
          :doc-label="'State License Supporting Document'"
          @create-document="formData.state_license_doc = $event"
          @delete-document="formData.state_license_doc = undefined"
        />
        <div v-if="error" class="alert alert-danger py-2 mb-2">Something went wrong. Please try again.</div>
        <div class="d-flex">
          <BushelBtn v-if="cancelable" class="btn-block mr-2" variant="secondary" flat @click="$emit('cancel')"> Cancel </BushelBtn>
          <BushelBtn class="btn-block" variant="success" :disabled="saving" @click="submit">
            <div v-if="saving" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            {{ submitLabel }}
          </BushelBtn>
        </div>
      </div>
      <SingleFileUploader
        id="state-license-uploader-desktop"
        input-id="stateLicenseDocDesktop"
        class="d-none d-lg-block col-6"
        :file-url="formData.state_license_doc"
        no-overlay
        :allow-delete="true"
        download-path="buyers/state-license-doc/download"
        :doc-label="'State License Supporting Document'"
        @create-document="formData.state_license_doc = $event"
        @delete-document="formData.state_license_doc = undefined"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SingleFileUploader from "@/components/documents/SingleFileUploader.vue";
import * as api from "@/services/api";

export default {
  components: { SingleFileUploader },
  props: {
    initialValues: {
      type: Object,
      default: null,
    },
    countries: {
      type: Array,
      default: () => [],
    },
    countriesLoading: {
      type: Boolean,
      default: false,
    },
    operationTypes: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    showCompanyFields: {
      type: Boolean,
      default: false,
    },
    cancelable: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: "Save Location",
    },
    error: {
      type: Boolean,
      default: false,
    },
    currentCompany: {
      type: Object,
      default: null,
    },
  },
  emits: ["submit", "cancel"],
  data() {
    return {
      industry: APP_INDUSTRY,
      states: [],
      statesLoading: false,
      submitted: false,
      formData: {
        name: "",
        operation_type: null,
        summary: "",
        line1: "",
        line2: "",
        city: "",
        state: null,
        country: null,
        zip: "",
        state_license: "",
        state_license_doc: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      storeCompany: "companies/currentCompany",
    }),
    usCannabisUser() {
      if (this.industry !== "cannabis") return false;
      if (this.storeCompany?.country && this.storeCompany.country.value !== "US") return false;
      return true;
    },
    stateLicenseRequired() {
      return this.usCannabisUser;
    },
  },
  watch: {
    countries(list) {
      if (this.initialValues || !this.currentCompany || !list.length || this.formData.country) return;
      const country = list.find((c) => c.id === this.currentCompany.country_id);
      if (!country) return;
      this.formData.country = country;
      this.fetchStates(country.id).then(() => {
        this.formData.state = this.states.find((s) => s.id === this.currentCompany.state_id) ?? null;
      });
    },
    initialValues: {
      immediate: true,
      handler(values) {
        if (!values) return;
        this.formData.name = values.name || "";
        this.formData.operation_type = values.operation_type || null;
        this.formData.summary = values.summary || "";
        this.formData.line1 = values.line1 || "";
        this.formData.line2 = values.line2 || "";
        this.formData.city = values.city || "";
        this.formData.zip = values.zip || "";
        this.formData.state_license = values.state_license || "";
        this.formData.state_license_doc = values.state_license_doc || "";

        const country = this.countries.find((c) => c.id === values.country?.id);
        if (country) {
          this.formData.country = country;
          this.fetchStates(country.id).then(() => {
            this.formData.state = this.states.find((s) => s.id === values.state?.id) ?? null;
          });
        } else {
          this.formData.country = values.country ?? null;
          this.formData.state = values.state ?? null;
        }
      },
    },
  },
  methods: {
    fetchStates(countryId) {
      this.statesLoading = true;
      return api.get(`states/by-country/${countryId}`).then((response) => {
        this.statesLoading = false;
        this.states = response.data;
      });
    },
    reset() {
      this.submitted = false;
      this.formData.name = "";
      this.formData.operation_type = null;
      this.formData.line1 = "";
      this.formData.line2 = "";
      this.formData.city = "";
      this.formData.zip = "";
      this.formData.state_license = "";
      this.formData.state_license_doc = "";
    },
    submit() {
      this.submitted = true;
      if (
        !this.formData.name ||
        !this.formData.operation_type ||
        !this.formData.line1 ||
        !this.formData.city ||
        !this.formData.state ||
        !this.formData.country ||
        !this.formData.zip ||
        (this.stateLicenseRequired && !this.formData.state_license)
      ) {
        return;
      }
      this.submitted = false;
      this.$emit("submit", { ...this.formData });
    },
  },
};
</script>
